import React, { useState, useEffect, useCallback } from 'react';
import { Box, VStack, HStack, Input, Select, Text, IconButton, useToast, Flex, Badge } from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiExternalLink } from 'react-icons/fi';
import api from '../api';

export default function SocialLinksEditor({ entityType, entityId, compact = false }) {
  const [networks, setNetworks] = useState([]);
  const [links, setLinks] = useState([]);
  const [addNet, setAddNet] = useState('');
  const [addHandle, setAddHandle] = useState('');
  const toast = useToast();

  const fetchData = useCallback(async () => {
    try {
      const [netRes, linkRes] = await Promise.all([
        api.get('/social/networks'),
        entityId ? api.get(`/social/links/${entityType}/${entityId}`) : Promise.resolve({ data: [] }),
      ]);
      setNetworks(netRes.data);
      setLinks(linkRes.data);
    } catch(e) {}
  }, [entityType, entityId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!addNet || !addHandle || !entityId) return;
    try {
      await api.post('/social/links', {
        entity_type: entityType, entity_id: entityId,
        social_network_id: parseInt(addNet), handle: addHandle,
      });
      setAddNet(''); setAddHandle('');
      await fetchData();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/social/links/${id}`);
    await fetchData();
  };

  if (!entityId) return null;

  return (
    <Box>
      <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em"
        bg="gray.800" color="white" px={1.5} py={0.5} borderRadius="sm" display="inline-block" mb={1}>
        Social Links
      </Text>
      {links.length > 0 && (
        <VStack spacing={1} align="stretch" mb={2}>
          {links.map(l => (
            <Flex key={l.id} align="center" gap={2} fontSize="11px">
              <Badge fontSize="8px" colorScheme="gray" minW="60px">{l.network_name}</Badge>
              <Text flex={1} noOfLines={1}>{l.handle}</Text>
              {l.url && <IconButton icon={<FiExternalLink />} size="xs" variant="ghost"
                onClick={() => window.open(l.url, '_blank')} aria-label="Open" />}
              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                onClick={() => handleDelete(l.id)} aria-label="Remove" />
            </Flex>
          ))}
        </VStack>
      )}
      <HStack spacing={1}>
        <Select size="xs" value={addNet} onChange={e => setAddNet(e.target.value)} flex={1} placeholder="Network...">
          {networks.filter(n => !links.some(l => l.social_network_id === n.id)).map(n => (
            <option key={n.id} value={n.id}>{n.name}</option>
          ))}
        </Select>
        <Input size="xs" value={addHandle} onChange={e => setAddHandle(e.target.value)}
          placeholder="Handle / URL" flex={2} />
        <IconButton icon={<FiPlus />} size="xs" colorScheme="blue" variant="outline"
          onClick={handleAdd} aria-label="Add" isDisabled={!addNet || !addHandle} />
      </HStack>
    </Box>
  );
}
